<template>
  <a-modal
    :visible="visible"
    title="Suggested Policy Preview"
    :width="960"
    :footer="null"
    :bodyStyle="{ maxHeight: '70vh', overflowY: 'auto' }"
    @cancel="$emit('close')"
  >
    <div v-if="!groups || groups.length === 0">
      No suggested rules found from the selected pages.
    </div>

    <template v-else-if="groups.length === 1">
      <PolicyGroupContent
        :group="groups[0]"
        :groupIndex="0"
        :selectedRules="selectedRules"
        :similarPolicies="getSimilarPoliciesForGroup(groups[0])"
        :actionHref="getActionHrefForGroup(groups[0], 0)"
        @toggle="onToggleRule(0, $event)"
        @proceed="onProceed(groups[0], 0, $event)"
        @proceedToExisting="onProceedToExisting(groups[0], $event)"
      />
    </template>

    <a-tabs v-else type="card" :activeKey="activeTabKey" @change="onTabChange">
      <a-tab-pane
        v-for="(group, index) in groups"
        :key="String(index)"
        :tab="getTabLabel(group, index)"
      >
        <PolicyGroupContent
          :group="group"
          :groupIndex="index"
          :selectedRules="selectedRules"
          :similarPolicies="getSimilarPoliciesForGroup(group)"
          :actionHref="getActionHrefForGroup(group, index)"
          @toggle="onToggleRule(index, $event)"
          @proceed="onProceed(group, index, $event)"
          @proceedToExisting="onProceedToExisting(group, $event)"
        />
      </a-tab-pane>
    </a-tabs>
  </a-modal>
</template>

<script>
import PolicyGroupContent from './PolicyGroupContent.vue';
import { getPolicyGroupUrlIds, normalizeRules } from '@shared/utils/policyRules';
import {
  extractHostname,
  getDomainSuffixes,
} from '@shared/utils/url';
import {
  generatePolicyPrefillHandle,
  removePolicyPrefill,
  storePolicyPrefill,
} from '@shared/utils/policyPrefill';

function buildSelectionKey(groupIndex, rule, ruleIndex) {
  return `${groupIndex}-${rule.type}-${rule.field}-${ruleIndex}`;
}

function getTabLabel(group, index) {
  if (group.mode === 'existing') {
    return `Policy ${group.policyName || `#${group.policyId}`} (${(group.urls || []).length})`;
  }
  return `New Policy ${index + 1} (${(group.urls || []).length})`;
}

function buildDomainSuffixSet(domains) {
  const suffixes = new Set();

  domains.forEach(domain => {
    getDomainSuffixes(domain).forEach(suffix => suffixes.add(suffix));
  });

  return suffixes;
}

function domainsOverlapPrecomputed(domain, domainSuffixes, exactDomains, relatedDomainSuffixes) {
  if (!domain) return false;

  if (relatedDomainSuffixes.has(domain)) {
    return true;
  }

  for (let i = 0; i < domainSuffixes.length; i++) {
    if (exactDomains.has(domainSuffixes[i])) return true;
  }
  return false;
}

export default {
  name: 'SuggestedPolicyModal',
  components: {
    PolicyGroupContent,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    groups: {
      type: Array,
      default: () => [],
    },
    allPolicies: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeTabKey: '0',
      selectedRules: {},
      prefillHandles: {},
    };
  },
  computed: {
    indexedPoliciesForSuggestion() {
      return (this.allPolicies || [])
        .map(policy => {
          const domains = new Set();

          (policy.urls || []).forEach(urlItem => {
            const urlStr = urlItem.url || urlItem.webPageUrl;
            if (!urlStr) return;

            const domain = extractHostname(urlStr);
            if (domain) domains.add(domain);
          });

          return {
            id: policy.id,
            name: policy.name || '',
            domains,
            domainSuffixes: buildDomainSuffixSet(domains),
          };
        })
        .filter(policy => policy.domains.size > 0);
    },
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.activeTabKey = '0';
        this.initializeSelectedRules();
        this.initializePrefillHandles();
        this.$nextTick(() => this.restageAllPrefills());
      } else {
        this.cleanupStagedPrefills();
      }
    },
    groups() {
      if (!this.visible) return;
      this.initializePrefillHandles();
      this.$nextTick(() => this.restageAllPrefills());
    },
    selectedRules: {
      deep: true,
      handler() {
        if (!this.visible) return;
        this.restageAllPrefills();
      },
    },
  },
  created() {
    // Non-reactive: handles currently mid-navigation. cleanupStagedPrefills() skips these
    // so the destination route can still read its prefill from localStorage after the
    // modal's `visible` watcher fires `close` ahead of `$router.push` resolving.
    this._inFlightPrefillHandles = new Set();
  },
  beforeDestroy() {
    this.cleanupStagedPrefills();
  },
  methods: {
    onTabChange(activeKey) {
      this.activeTabKey = activeKey;
    },
    getRuleKey(groupIndex, rule, ruleIndex) {
      return buildSelectionKey(groupIndex, rule, ruleIndex);
    },
    initializeSelectedRules() {
      const selected = {};
      (this.groups || []).forEach((group, groupIndex) => {
        const rules = group.mode === 'existing' ? (group.newRulesOnly || []) : (group.rules || []);
        rules.forEach((rule, ruleIndex) => {
          const key = this.getRuleKey(groupIndex, rule, ruleIndex);
          selected[key] = this.selectedRules[key] !== undefined ? this.selectedRules[key] : true;
        });
      });
      this.selectedRules = selected;
    },
    onToggleRule(groupIndex, ruleIndex) {
      const group = this.groups[groupIndex];
      const rules = group.mode === 'existing' ? (group.newRulesOnly || []) : (group.rules || []);
      const rule = rules[ruleIndex];
      const key = this.getRuleKey(groupIndex, rule, ruleIndex);
      const currentValue = this.selectedRules[key] !== false;
      this.$set(this.selectedRules, key, !currentValue);
    },
    getSelectedRules(group, groupIndex) {
      const rules = group.mode === 'existing' ? (group.newRulesOnly || []) : (group.rules || []);
      return rules.filter((rule, ruleIndex) => {
        const key = this.getRuleKey(groupIndex, rule, ruleIndex);
        return this.selectedRules[key] !== false;
      });
    },
    getTabLabel,
    getSimilarPoliciesForGroup(group) {
      if (group.mode !== 'new' || this.indexedPoliciesForSuggestion.length === 0) {
        return [];
      }

      const groupDomainEntries = [];
      const seenDomains = new Set();
      (group.urls || []).forEach(urlObj => {
        const urlStr = urlObj.url || urlObj.webPageUrl;
        if (!urlStr) return;
        const domain = extractHostname(urlStr);
        if (!domain || seenDomains.has(domain)) return;
        seenDomains.add(domain);
        groupDomainEntries.push({
          domain,
          suffixes: getDomainSuffixes(domain),
        });
      });

      if (groupDomainEntries.length === 0) return [];

      const similarPolicies = this.indexedPoliciesForSuggestion.reduce((matches, policy) => {
        const matchingDomains = [];
        for (let i = 0; i < groupDomainEntries.length; i++) {
          const entry = groupDomainEntries[i];
          if (domainsOverlapPrecomputed(entry.domain, entry.suffixes, policy.domains, policy.domainSuffixes)) {
            matchingDomains.push(entry.domain);
          }
        }

        if (matchingDomains.length > 0) {
          matches.push({
            id: policy.id,
            name: policy.name,
            matchingDomains,
          });
        }
        return matches;
      }, []);

      return similarPolicies;
    },
    initializePrefillHandles() {
      const handles = {};
      (this.groups || []).forEach((_, idx) => {
        handles[idx] = this.prefillHandles[idx] || generatePolicyPrefillHandle();
      });
      this.prefillHandles = handles;
    },
    cleanupStagedPrefills() {
      const inFlight = this._inFlightPrefillHandles || new Set();
      Object.values(this.prefillHandles || {}).forEach(handle => {
        if (inFlight.has(handle)) return;
        removePolicyPrefill(handle);
      });
      this.prefillHandles = {};

      // Sweep in-flight handles after a short grace period: by then the destination
      // component has mounted and consumed the entry. The timeout protects against
      // navigation cancellation or destination errors so we don't leak indefinitely.
      if (inFlight.size > 0) {
        const handles = Array.from(inFlight);
        inFlight.clear();
        setTimeout(() => {
          handles.forEach(handle => {
            removePolicyPrefill(handle);
          });
        }, 5000);
      }
    },
    buildPrefillPayload(group, groupIndex) {
      const selectedRules = this.getSelectedRules(group, groupIndex);
      if (!selectedRules.length) return null;

      const normalizedRules = normalizeRules(selectedRules);

      if (group.mode === 'existing') {
        return {
          mode: 'edit',
          policyId: String(group.policyId),
          rules: normalizedRules,
        };
      }

      return {
        mode: 'new',
        url_ids: getPolicyGroupUrlIds(group),
        rules: normalizedRules,
      };
    },
    stagePrefillForGroup(groupIndex) {
      const group = (this.groups || [])[groupIndex];
      const handle = this.prefillHandles[groupIndex];
      if (!group || !handle) return;

      const payload = this.buildPrefillPayload(group, groupIndex);
      try {
        if (payload) {
          storePolicyPrefill(handle, payload);
        } else {
          removePolicyPrefill(handle);
        }
      } catch (_) { /* ignore */ }
    },
    restageAllPrefills() {
      (this.groups || []).forEach((_, idx) => this.stagePrefillForGroup(idx));
    },
    getActionHrefForGroup(group, groupIndex) {
      const handle = this.prefillHandles[groupIndex];
      const base = group.mode === 'existing' ? `/policy/${group.policyId}` : '/policy/add';
      if (!handle) return base;
      return `${base}?prefill=${encodeURIComponent(handle)}`;
    },
    onProceed(group, groupIndex, options = {}) {
      const handle = this.prefillHandles[groupIndex];
      if (!handle) return;

      // Restage synchronously inside the click handler to capture the latest selection
      // before the browser navigates (matters most for new-tab gestures).
      this.stagePrefillForGroup(groupIndex);

      const payload = this.buildPrefillPayload(group, groupIndex);
      if (!payload) return;

      const openInNewTab = !!(options && options.openInNewTab);
      if (openInNewTab) {
        // The anchor's href already carries ?prefill=<handle>; the browser opens the new tab.
        // Keep the modal open in the current tab and leave the staged payload in place
        // until the user closes the modal (cleanupStagedPrefills) or the new tab consumes it.
        return;
      }

      // Same-tab navigation: $emit('close') will trigger cleanupStagedPrefills before
      // $router.push resolves and the destination mounts. Mark this handle in-flight so
      // cleanup skips it; the destination will removeItem on consume.
      this._inFlightPrefillHandles.add(handle);
      this.$emit('close');
      this.$router.push(this.getActionHrefForGroup(group, groupIndex));
    },
    onProceedToExisting(group, policyId) {
      const urlIds = getPolicyGroupUrlIds(group);

      if (urlIds.length === 0) return;

      const payload = {
        mode: 'edit',
        policyId: String(policyId),
        url_ids: urlIds,
      };
      const handle = generatePolicyPrefillHandle();
      try {
        storePolicyPrefill(handle, payload);
      } catch (_) { /* ignore */ }

      this._inFlightPrefillHandles.add(handle);
      this.$emit('close');
      this.$router.push(`/policy/${policyId}?prefill=${encodeURIComponent(handle)}`);
    },
  },
};
</script>
